import { useState, useMemo } from 'react'
import Fuse from 'fuse.js'
import { buildSearchIndex, type SearchableItem } from '@/lib/search-index'

export type SortOption = 'relevance' | 'title' | 'category'
export type CategoryFilter = 'all' | 'blog' | 'project' | 'service' | 'page'

export function useSearch() {
  const [query, setQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all')
  const [sortBy, setSortBy] = useState<SortOption>('relevance')

  // Build search index
  const searchIndex = useMemo(() => buildSearchIndex(), [])

  // Configure Fuse.js
  const fuse = useMemo(
    () =>
      new Fuse(searchIndex, {
        keys: [
          { name: 'title', weight: 2 },
          { name: 'tags', weight: 1.5 },
          { name: 'description', weight: 1 },
          { name: 'category', weight: 1.5 },
        ],
        threshold: 0.4,
        includeScore: true,
      }),
    [searchIndex]
  )

  // Perform search with category filtering and sorting
  const allResults = useMemo(() => {
    if (!query.trim()) return []

    const trimmedQuery = query.trim().toLowerCase()

    // Special handling for category-based searches
    const categoryMap: Record<string, 'blog' | 'project' | 'service'> = {
      blog: 'blog',
      blogs: 'blog',
      'blog posts': 'blog',
      'blog post': 'blog',
      project: 'project',
      projects: 'project',
      service: 'service',
      services: 'service',
    }

    let results: SearchableItem[] = []

    // If query matches a category, filter by category first
    const matchedCategory = categoryMap[trimmedQuery]
    if (matchedCategory) {
      results = searchIndex.filter(item => item.category === matchedCategory)
    } else {
      // Otherwise, use Fuse.js for fuzzy search
      const searchResults = fuse.search(query)
      results = searchResults.map(result => result.item)
    }

    // Apply category filter
    if (categoryFilter !== 'all') {
      results = results.filter(item => item.category === categoryFilter)
    }

    // Apply sorting
    if (sortBy === 'title') {
      results = [...results].sort((a, b) => a.title.localeCompare(b.title))
    } else if (sortBy === 'category') {
      results = [...results].sort((a, b) =>
        a.category.localeCompare(b.category)
      )
    }
    // 'relevance' is already sorted by Fuse.js score

    return results
  }, [query, fuse, searchIndex, categoryFilter, sortBy])

  // Calculate category breakdown
  const categoryBreakdown = useMemo(() => {
    const breakdown = {
      blog: 0,
      project: 0,
      service: 0,
      page: 0,
    }
    allResults.forEach(item => {
      breakdown[item.category]++
    })
    return breakdown
  }, [allResults])

  const results = useMemo(() => {
    return allResults.slice(0, 8)
  }, [allResults])

  return {
    query,
    setQuery,
    results, // Limited to 8 for dropdown
    allResults, // All results for search page
    isSearching: query.trim().length > 0,
    categoryFilter,
    setCategoryFilter,
    sortBy,
    setSortBy,
    categoryBreakdown,
  }
}
